import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

const RecentlyViewedItemSchema = z.object({
	id: z.string(),
	entityType: z.enum(["account", "bond", "bondRequest"]),
});

type RecentlyViewedItem = z.infer<typeof RecentlyViewedItemSchema>;

export const RECENTLY_VIEWED_STORAGE_KEY = "inrev_recently_viewed";
export const MAX_RECENTLY_VIEWED = 6;

function readFromStorage(): RecentlyViewedItem[] {
	try {
		const stored = localStorage.getItem(RECENTLY_VIEWED_STORAGE_KEY);
		if (!stored) return [];
		const result = z.array(RecentlyViewedItemSchema).safeParse(JSON.parse(stored));
		return result.success ? result.data : [];
	} catch {
		return [];
	}
}

export const useRecentlyViewedEntities = () => {
	const [recentlyViewedEntities, setRecentlyViewedEntities] =
		useState<RecentlyViewedItem[]>(readFromStorage);

	useEffect(() => {
		const handleStorage = (e: StorageEvent) => {
			if (e.key !== RECENTLY_VIEWED_STORAGE_KEY) return;
			setRecentlyViewedEntities(readFromStorage());
		};
		window.addEventListener("storage", handleStorage);
		return () => window.removeEventListener("storage", handleStorage);
	}, []);

	const addRecentlyViewedEntity = useCallback((item: RecentlyViewedItem) => {
		setRecentlyViewedEntities((prev) => {
			const isDuplicate = (entity: RecentlyViewedItem) =>
				entity.id === item.id && entity.entityType === item.entityType;
			const next = [item, ...prev.filter((entity) => !isDuplicate(entity))].slice(
				0,
				MAX_RECENTLY_VIEWED,
			);
			localStorage.setItem(RECENTLY_VIEWED_STORAGE_KEY, JSON.stringify(next));
			return next;
		});
	}, []);

	return {
		recentlyViewedEntities,
		addRecentlyViewedEntity,
	};
};
